import React, { useCallback } from 'react';
import { FileText, X, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { OnboardingFormData } from '../OnboardingWizardContent';

interface Props {
  data: OnboardingFormData;
  updateData: (updates: Partial<OnboardingFormData>) => void;
}

const StepDocuments: React.FC<Props> = ({ data, updateData }) => {
  const handleDocumentUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    updateData({ 
      documentFiles: [...data.documentFiles, ...files],
      documentUrls: [...data.documentUrls, ...files.map(f => URL.createObjectURL(f))]
    });
    e.target.value = '';
  }, [data.documentFiles, data.documentUrls, updateData]);

  const removeDocument = (index: number) => {
    updateData({
      documentFiles: data.documentFiles.filter((_, i) => i !== index),
      documentUrls: data.documentUrls.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        Upload FAQs, internal documentation, service descriptions, or any other materials 
        that will help your assistant answer questions accurately. <span className="text-sm">(Optional)</span>
      </p>

      {/* Document List */}
      {data.documentFiles.length > 0 && (
        <div className="space-y-2">
          {data.documentFiles.map((file, index) => (
            <div key={index} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <div className="p-2 bg-brand-teal/10 rounded">
                <FileText className="h-5 w-5 text-brand-teal" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeDocument(index)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
        <div className="flex flex-col items-center justify-center pt-5 pb-6">
          <Upload className="h-10 w-10 text-muted-foreground mb-3" />
          <p className="mb-2 text-sm text-muted-foreground">
            <span className="font-semibold">Click to upload</span> or drag and drop
          </p>
          <p className="text-xs text-muted-foreground">
            PDF, DOC, DOCX (Max 50MB each)
          </p>
        </div>
        <input 
          type="file" 
          className="hidden" 
          accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          multiple
          onChange={handleDocumentUpload}
        />
      </label>

      <div className="bg-brand-teal/5 border border-brand-teal/20 rounded-lg p-4">
        <p className="text-sm text-card-foreground">
          <strong>💡 Examples of helpful documents:</strong>
        </p>
        <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
          <li>Frequently Asked Questions (FAQs)</li>
          <li>Service descriptions and pricing guides</li>
          <li>Client onboarding materials</li>
          <li>Internal process documentation</li>
        </ul>
      </div>
    </div>
  );
};

export default StepDocuments;
